/**
 * El cupo del mes, en fila, como los contadores de conversaciones.  (spec 062)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useSoloLectura } from '../auth'
import { cn } from '../lib/cn'
import { api, ErrorApi } from '../lib/api'
import { useEsAdminUi } from '../ver-como'

interface PorPlantilla {
    nombre: string
    idioma: string
    enviadas: number
}

interface Cupo {
    mes: string
    linea: string | null
    maximo: number
    gastadas: number
    quedan: number
    diasHastaReset: number
    porPlantilla: PorPlantilla[]
    recordatoriosHasta: number
    quedarianAlMenos: number
}

function Pastilla({
    color,
    valor,
    titulo,
}: {
    color: string
    valor: number
    titulo: string
}) {
    return (
        <span className="inline-flex min-h-11 items-center gap-2 rounded-full border border-neutral-800 bg-neutral-900/60 px-3 py-1.5 text-sm md:min-h-0">
            <span className={cn('h-2 w-2 shrink-0 rounded-full', color)} />
            <span className="tabular-nums font-medium text-neutral-100">{valor}</span>
            <span className="text-neutral-400">{titulo}</span>
        </span>
    )
}

export function CupoPlantillas({ accountId }: { accountId: number }) {
    const esAdmin = useEsAdminUi()
    const soloLectura = useSoloLectura()
    const puedeEditar = esAdmin && !soloLectura
    const qc = useQueryClient()
    const { data, isPending, isError, error } = useQuery({
        queryKey: ['cupo-plantillas', accountId],
        queryFn: () => api.get<Cupo>(`/business/${accountId}/cupo-plantillas`),
    })
    const [borrador, setBorrador] = useState<string | null>(null)
    const guardar = useMutation({
        mutationFn: (maximo: number) =>
            api.patch<{ maximo: number }>(`/business/${accountId}/cupo-plantillas`, { maximo }),
        onSuccess: async () => {
            setBorrador(null)
            await qc.invalidateQueries({ queryKey: ['cupo-plantillas', accountId] })
        },
    })

    if (isPending) return <p className="text-sm text-neutral-500">Cargando el cupo de plantillas…</p>
    if (isError || !data) {
        return (
            <p className="text-sm text-rose-400">
                {error instanceof ErrorApi ? error.message : 'No se pudo cargar el cupo de plantillas.'}
            </p>
        )
    }

    const valor = borrador ?? String(data.maximo)
    const trozo = data.maximo > 0 ? Math.min(100, (data.gastadas / data.maximo) * 100) : 0

    return (
        <section className="space-y-3">
            <div className="flex h-3 overflow-hidden rounded-full bg-neutral-800" aria-hidden="true">
                {trozo > 0 && <div className="bg-sky-400" style={{ width: `${trozo}%` }} />}
            </div>
            <ul className="flex flex-wrap gap-2">
                <li>
                    <Pastilla color="bg-emerald-400" valor={data.quedan} titulo="quedan" />
                </li>
                <li>
                    <Pastilla color="bg-neutral-400" valor={data.diasHastaReset} titulo="días para el reinicio" />
                </li>
                <li>
                    <Pastilla color="bg-amber-400" valor={data.recordatoriosHasta} titulo="para recordatorios" />
                </li>
                <li>
                    <Pastilla color="bg-emerald-700" valor={data.quedarianAlMenos} titulo="al menos, después" />
                </li>
                <li>
                    <Pastilla color="bg-sky-400" valor={data.gastadas} titulo={`de ${data.maximo}`} />
                </li>
            </ul>
            {data.porPlantilla.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                    {data.porPlantilla.map((p) => (
                        <li key={`${p.nombre}::${p.idioma}`}>
                            <Pastilla color="bg-neutral-500" valor={p.enviadas} titulo={`${p.nombre} · ${p.idioma}`} />
                        </li>
                    ))}
                </ul>
            )}
            {(data.linea || puedeEditar) && (
                <div className="flex flex-wrap items-center gap-3">
                    {data.linea && <p className="text-xs text-neutral-500">Línea {data.linea}</p>}
                    {puedeEditar && (
                        <form
                            className="flex flex-wrap items-center gap-2"
                            onSubmit={(e) => {
                                e.preventDefault()
                                const n = Number(valor)
                                if (!Number.isInteger(n) || n < 0) return
                                guardar.mutate(n)
                            }}
                        >
                            <label className="flex items-center gap-2 text-sm text-neutral-400">
                                Máximo
                                <input
                                    inputMode="numeric"
                                    value={valor}
                                    onChange={(e) => setBorrador(e.target.value)}
                                    className="w-20 rounded-full border border-neutral-800 bg-neutral-900 px-3 py-1.5 tabular-nums text-neutral-100"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={guardar.isPending}
                                className="rounded-full border border-neutral-800 px-3 py-1.5 text-sm text-neutral-300 hover:border-neutral-600 disabled:text-neutral-600"
                            >
                                {guardar.isPending ? 'Guardando…' : 'Guardar'}
                            </button>
                            {guardar.isError && (
                                <p className="text-sm text-rose-400">
                                    {guardar.error instanceof ErrorApi ? guardar.error.message : 'No se pudo guardar'}
                                </p>
                            )}
                        </form>
                    )}
                </div>
            )}
        </section>
    )
}
